import { ApiOutlined, PlusOutlined, ShopOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Empty, Flex, Form, Input, List, Modal, Select, Space, Table, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { api, unwrap, type ChannelConnection, type ChannelPlatformName } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useWarehouses } from "@/lib/useWarehouses";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n, type MessageKey } from "@/i18n";
import { ConnectionDrawer } from "./ConnectionDrawer";
import { KeyConnectModal } from "./KeyConnectModal";
import { PLATFORMS, useConnect, useConnections, usePlatforms } from "./hooks";
import { PlatformLabel, PlatformLogo } from "./PlatformBadge";
import { ConnectionStatus } from "./StatusTag";

/** Turns the OAuth callback's ?connected= / ?error= into one message, once. */
export function useConnectResult() {
  const { t } = useI18n();
  const { message } = App.useApp();
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const connected = params.get("connected") as ChannelPlatformName | null;
    const error = params.get("error");
    if (!connected && !error) return;
    if (connected) {
      void message.success(t("channels.result.connected", { count: params.get("count") ?? "1", platform: t(`platform.${connected}`) }), 6);
      if (params.get("warning") === "shop_taken") void message.warning(t("channels.warning.shop_taken"), 8);
    } else if (error) {
      const key = `channels.result.${error}` as MessageKey;
      void message.error(t(["cancelled", "expired", "failed", "shop_taken"].includes(error) ? key : "channels.result.failed"), 8);
    }
    setParams({}, { replace: true });
  }, [params, setParams, message, t]);
}

export function ConnectDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const platforms = usePlatforms();
  const connect = useConnect();
  const available = new Map(platforms.data?.items.map((p) => [p.platform, p.available]));
  return (
    <Modal open={open} onCancel={onClose} footer={null} title={t("channels.pickPlatform")} width={520}>
      <Typography.Paragraph type="secondary">{t("channels.pickBody")}</Typography.Paragraph>
      <List
        loading={platforms.isPending}
        dataSource={PLATFORMS}
        renderItem={(p) => {
          const ok = available.get(p) ?? false;
          return (
            <List.Item
              actions={[
                ok ? (
                  <Button key="go" type="primary" loading={connect.isPending && connect.variables === p} onClick={() => connect.mutate(p, { onSuccess: onClose })}>
                    {t("onboarding.marketplace.connect")}
                  </Button>
                ) : (
                  <Tag key="soon">{t("channels.unavailable")}</Tag>
                ),
              ]}
            >
              <List.Item.Meta avatar={<PlatformLogo platform={p} />} title={t(`platform.${p}`)} />
            </List.Item>
          );
        }}
      />
    </Modal>
  );
}

export function Channels() {
  const { t, formatDateTime } = useI18n();
  const { me, can } = useAuth();
  const connections = useConnections();
  const connect = useConnect();
  const [picking, setPicking] = useState(false);
  const [addingManual, setAddingManual] = useState(false);
  const [openID, setOpenID] = useState<string | null>(null);
  useConnectResult();
  const tz = me?.workspace?.timezone;
  const manage = can("channels.manage");
  const items = connections.data?.items ?? [];

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("channels.title")}
        </Typography.Title>
        {manage && (
          <Space wrap>
            <Button icon={<ShopOutlined />} onClick={() => setAddingManual(true)}>
              {t("channels.manual.add")}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setPicking(true)}>
              {t("channels.connect")}
            </Button>
          </Space>
        )}
      </Flex>


      {!connections.isPending && items.length === 0 ? (
        <Card>
          <Empty
            image={<ApiOutlined style={{ fontSize: 48, color: "var(--z-brand)" }} />}
            description={
              <Space orientation="vertical" size={4}>
                <Typography.Text strong>{t("channels.empty")}</Typography.Text>
                <Typography.Text type="secondary">{t("channels.emptyBody")}</Typography.Text>
              </Space>
            }
          >
            {manage && (
              <Button type="primary" onClick={() => setPicking(true)}>
                {t("channels.connect")}
              </Button>
            )}
          </Empty>
        </Card>
      ) : (
        <Table<ChannelConnection>
          rowKey="id"
          loading={connections.isPending}
          dataSource={items}
          pagination={false}
          onRow={(c) => ({ onClick: () => setOpenID(c.id), style: { cursor: "pointer" } })}
          columns={[
            {
              title: t("common.name"),
              dataIndex: "name",
              render: (name: string, c) => (
                <Space orientation="vertical" size={2}>
                  <Typography.Text strong>{name || c.externalShopId}</Typography.Text>
                  <PlatformLabel platform={c.platform} />
                </Space>
              ),
            },
            {
              title: t("common.status"),
              dataIndex: "status",
              width: 200,
              render: (s: ChannelConnection["status"], c) => (
                <Space orientation="vertical" size={2}>
                  <ConnectionStatus status={s} />
                  {c.statusReason === "bind_pending" && <Typography.Text type="secondary">{t("channels.bindPending")}</Typography.Text>}
                </Space>
              ),
            },
            {
              title: t("channels.lastSync"),
              dataIndex: "lastSyncAt",
              width: 200,
              render: (v: string | null, c) => (c.platform === "manual" ? "—" : v ? formatDateTime(v, tz) : t("common.never")),
            },
            {
              title: t("common.actions"),
              width: 180,
              render: (_, c) =>
                c.status === "reauth_required" && manage ? (
                  <Button
                    size="small"
                    type="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      connect.mutate(c.platform);
                    }}
                  >
                    {t("channels.reconnect")}
                  </Button>
                ) : (
                  <Button size="small" onClick={(e) => (e.stopPropagation(), setOpenID(c.id))}>
                    {t("channels.detail")}
                  </Button>
                ),
            },
          ]}
        />
      )}

      <ConnectDialog open={picking} onClose={() => setPicking(false)} />
      <ManualChannelModal open={addingManual} onClose={() => setAddingManual(false)} />
      <KeyConnectModal />
      <ConnectionDrawer id={openID} onClose={() => setOpenID(null)} />
    </>
  );
}

/** A sales channel without a marketplace: brand.com, Instagram, WhatsApp, offline. */
function ManualChannelModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const warehouses = useWarehouses();
  const [form] = Form.useForm<{ name: string; warehouseId?: string }>();
  const create = useMutation({
    mutationFn: (v: { name: string; warehouseId?: string }) =>
      unwrap(api.POST("/v1/channels/manual", { body: { name: v.name.trim(), warehouseId: v.warehouseId || null } })),
    onSuccess: () => {
      void message.success(t("channels.manual.added"));
      void queryClient.invalidateQueries({ queryKey: ["channels"] });
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal open={open} title={t("channels.manual.add")} okText={t("common.save")} cancelText={t("common.cancel")} confirmLoading={create.isPending} onOk={() => form.submit()} onCancel={onClose} destroyOnHidden>
      <Typography.Paragraph type="secondary">{t("channels.manual.help")}</Typography.Paragraph>
      <Form form={form} layout="vertical" requiredMark={false} onFinish={(v) => create.mutate(v)}>
        <Form.Item name="name" label={t("common.name")} rules={[{ required: true, min: 2, message: t("field.required") }]}>
          <Input maxLength={80} placeholder={t("channels.manual.namePlaceholder")} autoFocus />
        </Form.Item>
        <Form.Item name="warehouseId" label={t("channels.warehouse")}>
          <Select
            allowClear
            placeholder={t("channels.warehouseDefault")}
            options={warehouses.active.map((w) => ({ value: w.id, label: w.isDefault ? `${w.name} ★` : w.name }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
